// src/pages/erp/fiscal/fiscal-settings.schemas.ts
import { z } from "zod";
import i18n from "@/i18n";

const t = (key: string) => i18n.t(key, { ns: "validation" });

export const GeneralSchema = z
  .object({
    // CNPJ alfanumérico (Jul/2026): aceita letras e números, sem máscara, 14 chars
    cnpj: z
      .string()
      .transform((v) => v.replace(/[.\-/\s]/g, "").toUpperCase())
      .pipe(z.string().length(14, t("cnpjLength"))),
    nome: z.string().min(1, t("companyNameRequired")),
    nome_fantasia: z.string().optional(),
    email: z.string().email(t("emailInvalid")),
    telefone: z.string().min(10, t("phoneMinDigits")),
    regime_tributario: z.enum(["1", "2", "3", "4"], {
      required_error: t("taxRegimeRequired"),
    }),
    inscricao_estadual: z.string().optional(),
    inscricao_municipal: z.string().optional(),
    logradouro: z.string().min(1, t("streetRequired")),
    numero: z.string().min(1, t("addressNumberRequired")),
    complemento: z.string().optional(),
    bairro: z.string().min(1, t("neighborhoodRequired")),
    cep: z.string().length(8, t("cepEightDigits")),
    municipio: z.string().min(1, t("municipalityRequired")),
    codigo_municipio_emitente: z.string().length(7, t("ibgeCodeSevenDigits")),
    uf: z.string().length(2, t("ufLength")),
    arquivo_certificado_base64: z.string().optional(),
    senha_certificado: z.string().optional(),
  })
  .refine(
    (d) => !d.arquivo_certificado_base64 || !!d.senha_certificado,
    { message: t("certificatePasswordRequired"), path: ["senha_certificado"] },
  );

export type GeneralFormValues = z.infer<typeof GeneralSchema>;

export const NfseSchema = z.object({
  // Flags FocusNFe
  habilita_nfse: z.boolean().optional(),
  habilita_nfsen_homologacao: z.boolean().optional(),
  habilita_nfsen_producao: z.boolean().optional(),
  // Defaults locais
  default_nfse_service_list_item: z.string().min(1, t("serviceListItemRequired")),
  default_nfse_service_code: z.string().optional(),
  default_nfse_cnae: z.string().optional(),
  default_nfse_iss_rate: z
    .number({ invalid_type_error: t("issNumber") })
    .min(0.01, t("issGreaterThanZero"))
    .max(100, t("issMax")),
  default_nfse_natureza_operacao: z.enum(["1", "2", "3", "4", "5", "6"], {
    required_error: t("operationNatureRequired"),
  }),
  default_nfse_regime_especial_tributacao: z.enum(["", "1", "2", "3", "4", "5", "6"]).optional(),
  default_nfse_optante_simples_nacional: z.boolean(),
  default_nfse_iss_retido: z.boolean(),
  default_nfse_series: z.string().optional(),
  default_nfse_ibs_cbs_situacao_tributaria: z.string().optional(),
  default_nfse_ibs_cbs_classificacao_tributaria: z.string().optional(),
  default_nfse_ibs_uf_aliquota: z.number().min(0).max(100).nullable().optional(),
  default_nfse_ibs_mun_aliquota: z.number().min(0).max(100).nullable().optional(),
  default_nfse_cbs_aliquota: z.number().min(0).max(100).nullable().optional(),
});

export type NfseFormValues = z.infer<typeof NfseSchema>;

/** NfceSchema é criado em runtime recebendo environment para validar CSC condicionalmente */
export function buildNfceSchema(environment: "homologacao" | "producao") {
  return z
    .object({
      habilita_nfce: z.boolean(),
      csc_nfce_producao: z.string().optional(),
      id_token_nfce_producao: z.number().nullable().optional(),
      csc_nfce_homologacao: z.string().optional(),
      id_token_nfce_homologacao: z.number().nullable().optional(),
      default_nfce_operation_nature: z.string().min(1, t("operationNatureRequired")),
      codigo_municipio_emitente: z.string().length(7, t("ibgeCodeSevenDigits")),
      regime_tributario_emitente: z.enum(["1", "2", "3", "4"], {
        required_error: t("taxRegimeRequired"),
      }),
      default_nfce_series: z.string().optional(),
    })
    .superRefine((d, ctx) => {
      if (!d.habilita_nfce) return;
      if (environment === "producao") {
        if (!d.csc_nfce_producao) {
          ctx.addIssue({ code: "custom", path: ["csc_nfce_producao"], message: t("productionCscRequired") });
        }
        if (d.id_token_nfce_producao == null) {
          ctx.addIssue({ code: "custom", path: ["id_token_nfce_producao"], message: t("productionTokenIdRequired") });
        }
      } else {
        if (!d.csc_nfce_homologacao) {
          ctx.addIssue({ code: "custom", path: ["csc_nfce_homologacao"], message: t("stagingCscRequired") });
        }
        if (d.id_token_nfce_homologacao == null) {
          ctx.addIssue({ code: "custom", path: ["id_token_nfce_homologacao"], message: t("stagingTokenIdRequired") });
        }
      }
    });
}

export type NfceFormValues = z.infer<ReturnType<typeof buildNfceSchema>>;
